"use client"
import styled from "@emotion/styled"
import { ComparisonBox, CustomBreadcrumbs } from "@eurodrive/ui"
import { filterManufacturersBySelection } from "@eurodrive/utils/filterManufacturersBySelection"
import { SearchPageMetadata } from "@eurodrive/utils/metadataTypes"
import {
  transformCarFeatures,
  transformCarTypes,
} from "@eurodrive/utils/transformMetadataFilters"
import { useQuery } from "@tanstack/react-query"
import type { LeasingCarDto, SaleCarDto } from "api"
import { CarType } from "./Filters/types"
import { Suspense, useEffect, useMemo, useRef, useState } from "react"
import CarResults from "./CarResults"
import ContentFilters from "./ContentFilters"
import Filters from "./Filters"
import { useSearchFilters } from "./Filters/useSearchFilters"
import MobileFiltersDrawer from "./MobileFiltersDrawer"
import { searchCars } from "./searchCars"

const StyledLayout = styled.div`
  padding: 0 20px 24px 20px;
  width: 100%;
  margin: auto;
  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 0 120px 64px 120px;
  }
`

const OnlyDesktop = styled.div`
  display: none;

  ${(props) => props.theme.breakpoints.up("md")} {
    display: block;
  }
`

const StyledGrid = styled.div`
  display: grid;
  ${(props) => props.theme.breakpoints.up("md")} {
    grid-template-columns: 248px 1fr;
  }
  gap: 16px;
`

const ContentLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  min-width: 0;
`

const SearchSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  padding-top: 64px;
`

interface SearchPageProps {
  metadata: SearchPageMetadata
  typeImages?: { carName: string; imageNormal: string; imageOn: string }[]
  comparisonTexts: any
  /** Smart default carType when URL is unfiltered leasing (has inventory) */
  defaultCarType?: CarType
  leasingImageMap?: Record<string, string>
}

export type DealType = "new" | "used" | "leasing"

export const dealTypeOptions: { value: DealType; label: string }[] = [
  { value: "new", label: "0 ק״מ" },
  { value: "used", label: "רכב יד שנייה" },
  { value: "leasing", label: "ליסינג" },
]

export default function SearchPage({
  metadata,
  typeImages = [],
  comparisonTexts,
  defaultCarType,
  leasingImageMap,
}: SearchPageProps) {
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <SearchPageContent
        metadata={metadata}
        typeImages={typeImages}
        comparisonTexts={comparisonTexts}
        defaultCarType={defaultCarType}
        leasingImageMap={leasingImageMap}
      />
    </Suspense>
  )
}

import { useManufacturerOptions } from "./useManufacturerOptions"
import { Typography } from "@mui/material"

// Component that contains the actual search logic
function SearchPageContent({
  metadata,
  typeImages = [],
  comparisonTexts,
  defaultCarType,
  leasingImageMap,
}: SearchPageProps) {
  const [currentPage, setCurrentPage] = useState(0)
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false)

  // Track if we've used the initial data already - only use it once on mount
  const hasUsedInitialData = useRef(false)

  // Handle mobile filter drawer
  const handleMobileFilterClick = () => {
    setIsMobileFiltersOpen(true)
  }

  const handleMobileFiltersClose = () => {
    setIsMobileFiltersOpen(false)
  }

  // Pass metadata to useSearchFilters so it can determine correct initial price range
  const { filters, debouncedFilters, actions } = useSearchFilters(
    undefined,
    metadata,
    defaultCarType,
  )

  // Select the metadata set for the active deal type. Sale is scoped by new/used;
  // leasing has no new/used split.
  const activeMetadataSet =
    filters.dealType === "leasing"
      ? metadata.leasing
      : metadata.sale[filters.dealType]

  const currentMetadata = activeMetadataSet[filters.carType]

  // Reset price range when metadata changes (deal type or car type switches)
  const prevMetadataKey = useRef<string>(
    `${filters.dealType}-${filters.carType}`,
  )

  useEffect(() => {
    const currentKey = `${filters.dealType}-${filters.carType}`
    if (prevMetadataKey.current !== currentKey) {
      prevMetadataKey.current = currentKey

      // Get fresh metadata for the new key
      const activeSet =
        filters.dealType === "leasing"
          ? metadata.leasing
          : metadata.sale[filters.dealType]
      const freshMetadata = activeSet[filters.carType]

      // Always reset price range when switching categories/deal types
      // This block only runs when prevMetadataKey !== currentKey (not on initial load)
      actions.setPriceRange([
        freshMetadata.minimumPrice,
        freshMetadata.maximumPrice,
      ])
    }
  }, [
    filters.dealType,
    filters.carType,
    filters.priceRange,
    metadata,
    actions.setPriceRange,
  ])

  // Transform metadata - use currentMetadata based on carType filter
  const transformedCarFeatures = useMemo(() => {
    return transformCarFeatures(currentMetadata.tags || [])
  }, [currentMetadata.tags])

  const transformedCarTypes = useMemo(() => {
    return transformCarTypes(currentMetadata.types || [], typeImages)
  }, [currentMetadata.types, typeImages])

  // Filter manufacturers based on selected features (tags), car types, and dealType
  const filteredManufacturers = useMemo(() => {
    // Convert string arrays to number arrays
    const selectedFeatures = filters.selectedFeatures
      .filter((f) => f)
      .map((f) => parseInt(f))
    const selectedCarTypes = filters.selectedCarTypes
      .filter((t) => t)
      .map((t) => parseInt(t))

    return filterManufacturersBySelection(currentMetadata, {
      selectedTypes: selectedCarTypes,
      selectedTags: selectedFeatures,
    })
  }, [currentMetadata, filters.selectedFeatures, filters.selectedCarTypes])

  const transformedManufacturers = useManufacturerOptions(
    metadata,
    filteredManufacturers,
    filters.selectedManufacturer,
  )

  // Auto-reset manufacturer/model when they become unavailable due to filter changes
  useEffect(() => {
    // Check availability against the transformed list which includes the "injected" manufacturer
    const manufacturerAvailable = transformedManufacturers.some(
      (m) => m.value === filters.selectedManufacturer,
    )

    // For model, we need to check if the model exists in the selected manufacturer's models
    let modelAvailable = false
    if (filters.selectedManufacturer && filters.selectedModel) {
      const manufacturer = transformedManufacturers.find(
        (m) => m.value === filters.selectedManufacturer,
      )
      if (manufacturer) {
        modelAvailable = manufacturer.models.some(
          (m) => m.value === filters.selectedModel,
        )
      }
    } else {
      modelAvailable = true // No model selected, so it's "available"
    }

    if (!manufacturerAvailable && filters.selectedManufacturer) {
      // Reset both manufacturer and model
      actions.setSelectedManufacturer("")
      actions.setSelectedModel("")
    } else if (!modelAvailable && filters.selectedModel) {
      // Reset only model
      actions.setSelectedModel("")
    }
  }, [
    transformedManufacturers,
    filters.selectedManufacturer,
    filters.selectedModel,
    actions,
  ])

  // Generate brands data for PreviouslyViewed component
  const brandsData = useMemo(() => {
    return transformedManufacturers.map((manufacturer) => ({
      image: "", // You might want to add manufacturer images later
      title: manufacturer.label,
      logo: "", // You might want to add manufacturer logos later
      url: `/search?manufacturer=${manufacturer.value}`,
    }))
  }, [transformedManufacturers])

  // Get model options for the selected manufacturer
  const modelOptions = useMemo(() => {
    if (!filters.selectedManufacturer) return []
    const selectedManufacturer = transformedManufacturers.find(
      (m) => m.value === filters.selectedManufacturer,
    )
    return selectedManufacturer?.models || []
  }, [filters.selectedManufacturer, transformedManufacturers])

  // Single query that fetches the current page - use debouncedFilters for API calls
  const {
    data: searchResults,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ["searchCars", debouncedFilters, currentPage],
    queryFn: async () => {
      // Mark that we've moved past initial data
      hasUsedInitialData.current = true
      return await searchCars(debouncedFilters, currentPage)
    },
    // Use placeholderData to show initial results immediately without blocking refetch
    placeholderData: (previousData) => {
      return previousData
    },
    staleTime: 0, // Always refetch when filters change
    refetchOnMount: true,
  })

  // Apply client-side discount filter (doesn't trigger API refetch)
  const allCarsBeforeDiscount = searchResults?.items || []
  const allCars = useMemo(() => {
    if (!filters.showDiscountsOnly) return allCarsBeforeDiscount

    return allCarsBeforeDiscount.filter((car) => {
      // Type guard for leasing cars
      if ("monthlyPaymentSalePriceIncludingVat" in car) {
        const salePrice = (car as LeasingCarDto)
          .monthlyPaymentSalePriceIncludingVat
        const regularPrice = (car as LeasingCarDto)
          .monthlyPaymentPriceIncludingVat
        return salePrice && regularPrice && salePrice < regularPrice
      }
      // For sale cars, check discountedPrice vs advertisedPrice
      if ("discountedPrice" in car) {
        const discountedPriceStr = (car as SaleCarDto).discountedPrice || ""
        const advertisedPriceStr = (car as SaleCarDto).advertisedPrice || ""

        // Only consider it a discount if discountedPrice is not empty and is a valid number
        if (!discountedPriceStr || discountedPriceStr.trim() === "") {
          return false
        }

        const salePrice = parseFloat(discountedPriceStr)
        const regularPrice = parseFloat(advertisedPriceStr)

        // Check both are valid numbers and sale price is less than regular price
        return (
          !isNaN(salePrice) &&
          !isNaN(regularPrice) &&
          salePrice > 0 &&
          regularPrice > 0 &&
          salePrice < regularPrice
        )
      }
      return false
    })
  }, [allCarsBeforeDiscount, filters.showDiscountsOnly])

  const totalCount = searchResults?.totalCount || 0
  // The discount filter is applied client-side over the fetched results, so the server's
  // totalCount no longer reflects what's actually shown. Use the filtered count when the
  // discount filter is active so the "מצאנו N רכבים" header always agrees with the list
  // (otherwise the header can claim N cars while the list renders the empty state).
  const displayCount = filters.showDiscountsOnly ? allCars.length : totalCount
  const hasNextPage = searchResults?.hasNextPage || false

  // Reset pagination when debouncedFilters change
  useEffect(() => {
    setCurrentPage(0)
  }, [debouncedFilters])

  const handleLoadMore = () => {
    if (hasNextPage) {
      setCurrentPage((prev) => prev + 1)
    }
  }

  return (
    <StyledLayout>
      <StyledGrid>
        <OnlyDesktop>
          <Filters
            filters={filters}
            actions={actions}
            carFeatures={transformedCarFeatures}
            carTypes={transformedCarTypes}
            manufacturers={transformedManufacturers}
            minPrice={currentMetadata.minimumPrice}
            maxPrice={currentMetadata.maximumPrice}
          />
        </OnlyDesktop>
        <ContentLayout>
          <SearchSection>
            <Typography
              variant="h1"
              component="h1"
              style={{
                position: "absolute",
                width: 1,
                height: 1,
                padding: 0,
                margin: -1,
                overflow: "hidden",
                clip: "rect(0, 0, 0, 0)",
                whiteSpace: "nowrap",
                border: 0,
              }}
            >
              תוצאות חיפוש רכבים
            </Typography>
            <CustomBreadcrumbs
              breadcrumbs={{
                selfObject: { title: "תוצאות חיפוש", url: "/search" },
                parents: [{ title: "ראשי", url: "/" }],
              }}
            />
            <ContentFilters
              filters={filters}
              actions={actions}
              resultsCount={displayCount}
              carTypes={transformedCarTypes}
              carFeatures={transformedCarFeatures}
              dealTypeOptions={dealTypeOptions}
              manufacturerOptions={transformedManufacturers}
              modelOptions={modelOptions}
              onMobileFilterClick={handleMobileFilterClick}
              minPrice={currentMetadata.minimumPrice}
              maxPrice={currentMetadata.maximumPrice}
            />
          </SearchSection>
          <CarResults
            comparisonBlock={comparisonTexts?.[0]}
            cars={allCars}
            totalCount={displayCount}
            currentCount={allCars.length}
            onLoadMore={handleLoadMore}
            hasMoreItems={hasNextPage}
            brands={brandsData}
            loading={isLoading || isFetching}
            isLeasing={filters.dealType === "leasing"}
            leasingImageMap={leasingImageMap}
          />
        </ContentLayout>
      </StyledGrid>

      {/* Comparison Box - shows when 2+ cars are selected */}
      <ComparisonBox comparisonBlock={comparisonTexts?.[0]} searchPage={true} />

      {/* Mobile Filters Drawer */}
      <MobileFiltersDrawer
        open={isMobileFiltersOpen}
        onClose={handleMobileFiltersClose}
        filters={filters}
        actions={actions}
        carFeatures={transformedCarFeatures}
        carTypes={transformedCarTypes}
        manufacturers={transformedManufacturers}
        resultsCount={displayCount}
        minPrice={currentMetadata.minimumPrice}
        maxPrice={currentMetadata.maximumPrice}
      />
    </StyledLayout>
  )
}
